import { type ReactNode, useMemo, useState } from 'react';
import {
  SortableDragHandle,
  SortableTableRow,
  SortableTableWrapper,
} from 'components/SortableTable';
import _ from 'lodash';
import { availabilityRelationsSelector } from 'slices/availabilityRelation.slice';
import { membershipsSelector } from 'slices/memberships.slice';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import type { TTypeNode } from 'utils/objectTypes';
import {
  EMandatoryProperty,
  EObjectProperty,
  EOptionalRelation,
  EReservationKind,
  ETemplateTypesText,
} from '@timeedit/types/lib/enums';
import type {
  TAvailabilityRelationType,
  TMemberType,
  TMemberTypesItem,
  TReservationTemplate,
  TTemplateType,
  TType,
} from '@timeedit/types/lib/types';
import {
  Button,
  type Column,
  EditableTable,
  Icon,
  TEInput,
} from '@timeedit/ui-components';
import {
  Checkbox,
  Dropdown,
  Space,
  Tooltip,
} from '@timeedit/ui-components/antd';
import type { CheckboxChangeEvent } from '@timeedit/ui-components/antd/checkbox/Checkbox';
import './ObjectTypesSection.scss';

const ColumnTitle = ({
  title,
  tooltip,
}: {
  title: ReactNode;
  tooltip: ReactNode;
}) => (
  <>
    {title}{' '}
    <Tooltip title={tooltip}>
      <Icon id="QuestionCircleOutlined" alt="question" />
    </Tooltip>
  </>
);

interface ObjectTypesSectionProps {
  template: TReservationTemplate & { maxNumberOfObjects?: number };
  onChange: (key: string, value: any) => void;
}

type TDataItem = TTemplateType & { id: string | number; name: string };

const stripIdName = (row: TDataItem | TTemplateType): TTemplateType => {
  const stripped: any = { ...row };
  delete stripped.id;
  delete stripped.name;
  return stripped;
};

const ObjectTypesSection = (props: ObjectTypesSectionProps) => {
  const { template, onChange } = props;
  const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
  const allTypes: TType[] = useAppSelector(selectObjectTypesList);
  const memberTypes: TMemberType[] = useAppSelector(membershipsSelector);
  const availabilityTypes: TAvailabilityRelationType[] = useAppSelector(
    availabilityRelationsSelector
  );
  const typeById = useAppSelector(selectObjectTypesMap);
  const typeTree = useAppSelector(selectObjectTypesTree);

  const templateTypes = template.types ?? [];
  const typeIds = templateTypes.map((type) => type.type);

  const data: TDataItem[] = useMemo(
    () =>
      templateTypes.map((type) => ({
        ...type,
        id: type.type,
        name: _.get(typeById, [type.type!, 'name'], '-'),
      })),
    [templateTypes, typeById]
  );

  const updateField = (rowId: string | number, key: string, value: any) => {
    const index = data.findIndex((d) => String(d.id) === String(rowId));
    if (index < 0) return;
    const newData = data.map(stripIdName);
    (newData[index] as any)[key] = value;
    onChange('types', newData);
  };

  const enumOptions = (e: Record<string, string>) =>
    Object.values(e).map((val) => ({ value: val, label: val }));

  // Controlled checkbox cell. The library's `input: { type: 'checkbox' }`
  // renders an uncontrolled checkbox (`defaultChecked`) that only reads its
  // value at mount, so it never re-syncs with the reactive template data.
  const renderCheckbox = (key: string) => (value: boolean, row: TDataItem) => (
    <Checkbox
      checked={!!value}
      data-testid={`object-${key}`}
      onChange={(e: CheckboxChangeEvent) =>
        updateField(row.id, key, e.target.checked)
      }
    />
  );

  const columns: Column<TDataItem>[] = [
    {
      key: 'dragHandle',
      width: '32px',
      render: () => <SortableDragHandle />,
    },
    {
      title: 'Object type',
      tooltip: 'Object type',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Count',
      tooltip: 'Count',
      dataIndex: 'max',
      key: 'max',
      render: (value: number, row: TDataItem) => (
        <TEInput.InputNumber
          value={value}
          max={template.maxNumberOfObjects}
          className="input-width"
          data-testid="object-count"
          onChange={(val) => updateField(row.id, 'max', val)}
        />
      ),
    },
    {
      title: 'Kind',
      tooltip: 'Kind',
      dataIndex: 'reservationKind',
      key: 'reservationKind',
      input: { type: 'select', options: enumOptions(EReservationKind) },
    },
    {
      title: 'Occurance',
      tooltip: 'Occurance',
      dataIndex: 'mandatoryProperty',
      key: 'mandatoryProperty',
      input: { type: 'select', options: enumOptions(EMandatoryProperty) },
    },
    {
      title: 'Properties',
      tooltip: 'Properties',
      dataIndex: 'objectProperty',
      key: 'objectProperty',
      input: { type: 'select', options: enumOptions(EObjectProperty) },
    },
    {
      title: 'Members',
      tooltip: 'Member Types',
      dataIndex: 'memberType',
      key: 'memberType',
      render: (value: number, row: TDataItem) => (
        <TEInput.Select
          value={value === 0 ? undefined : value}
          options={memberTypes
            .filter((memberType) => {
              const typeNode: TTypeNode = typeTree[row.type];
              const objectTypeIds = typeNode?.descendants
                .concat(typeNode.ancestors)
                .concat(typeNode.id) ?? [row.type];
              return memberType.memberTypes
                .map((mtItem: TMemberTypesItem) => mtItem.type)
                .some((id: number) => objectTypeIds.includes(id));
            })
            .map((type) => ({ value: type.id, label: type.name }))}
          data-testid="object-member"
          onChange={(val: number) => updateField(row.id, 'memberType', val)}
        />
      ),
    },
    {
      title: 'Availability',
      tooltip: 'Availability Types',
      dataIndex: 'availabilityType',
      key: 'availabilityType',
      input: {
        type: 'select',
        options: availabilityTypes.map((t) => ({
          value: String(t.id),
          label: t.name,
        })),
      },
    },
    {
      title: 'Request type',
      tooltip: 'Request type',
      dataIndex: 'requestType',
      key: 'requestType',
      render: renderCheckbox('requestType'),
    },
    {
      title: 'Canc. perm',
      tooltip: 'Cancel No Permission',
      dataIndex: 'cancelNoPermisson',
      key: 'cancelNoPermisson',
      render: renderCheckbox('cancelNoPermisson'),
    },
    {
      title: 'Opt. rel.',
      tooltip: 'Optional Relation',
      dataIndex: 'optionalRelation',
      key: 'optionalRelation',
      input: { type: 'select', options: enumOptions(EOptionalRelation) },
    },
    {
      title: 'Auto. obj',
      tooltip: 'Automatic Object Selection',
      dataIndex: 'automaticObjectSelection',
      key: 'automaticObjectSelection',
      render: renderCheckbox('automaticObjectSelection'),
    },
    {
      title: 'Padding',
      tooltip: 'Padding',
      dataIndex: 'padding',
      key: 'padding',
      render: renderCheckbox('padding'),
    },
    {
      title: 'Dyn. avail.',
      tooltip: 'Dynamic Availability',
      dataIndex: 'dynamicAvailability',
      key: 'dynamicAvailability',
      render: renderCheckbox('dynamicAvailability'),
    },
    {
      title: 'Res. text',
      tooltip: 'Reservation Text',
      dataIndex: 'reservationText',
      key: 'reservationText',
      input: { type: 'select', options: enumOptions(ETemplateTypesText) },
    },
    {
      title: 'Obst. text',
      tooltip: 'Obstacle Text',
      dataIndex: 'obstacleText',
      key: 'obstacleText',
      input: { type: 'select', options: enumOptions(ETemplateTypesText) },
    },
    {
      title: 'Size reservation type',
      tooltip: 'Request type',
      dataIndex: 'sizeReservationType',
      key: 'sizeReservationType',
      render: renderCheckbox('sizeReservationType'),
    },
  ];

  const columnsWithTooltips = columns.map((col) =>
    col.tooltip
      ? {
          ...col,
          title: <ColumnTitle title={col.title} tooltip={col.tooltip} />,
        }
      : col
  );

  const unselectedTypes = allTypes
    .filter((type) => !typeIds.includes(type.id))
    .map((type) => ({ key: type.id, label: type.name }));

  const addTypes = (id: number) => {
    onChange('types', [
      ...templateTypes,
      {
        type: id,
        min: 1,
        max: 1,
        reservationKind: 'ABSTRACT',
        mandatoryProperty: 'OPTIONAL',
        objectProperty: 'INCOMPLETE',
        memberType: undefined,
        availabilityType: undefined,
        requestType: false,
        cancelNoPermisson: false,
        optionalRelation: 'NONE',
        automaticObjectSelection: false,
        reservationText: 'STANDARD',
        obstacleText: 'STANDARD',
        padding: false,
        dynamicAvailability: false,
      },
    ]);
  };

  const removeTypes = () => {
    onChange(
      'types',
      templateTypes.filter(
        (type) => !selectedTypeIds.includes(type.type as number)
      )
    );
  };

  return (
    <div className="object-types">
      <SortableTableWrapper
        items={data}
        getId={(row) => row.id}
        onReorder={(newRows) => onChange('types', newRows.map(stripIdName))}
      >
        <EditableTable<TDataItem>
          rowKey="id"
          dataSource={data}
          columns={columnsWithTooltips}
          pagination={false}
          size="small"
          components={{
            body: { row: SortableTableRow },
            header: {
              cell: ({
                onResizeCallback: _onResizeCallback,
                onResizeStop: _onResizeStop,
                ...rest
              }: any) => <th {...rest} />,
            },
          }}
          rowSelection={{
            type: 'checkbox',
            onChange: (_keys, selectedRows) =>
              setSelectedTypeIds(
                selectedRows.map((type) => type.type as number)
              ),
          }}
          onSelectChange={(columnKey, record, option) => {
            const raw = option?.value;
            const coerced =
              columnKey === 'availabilityType' && raw !== undefined
                ? Number(raw)
                : raw;
            updateField(record.id, columnKey as string, coerced);
          }}
          footer={() => (
            <Space>
              <Dropdown
                menu={{
                  items: unselectedTypes,
                  onClick: ({ key }) => addTypes(Number(key)),
                }}
                trigger={['click']}
              >
                <Button type="primary" size="small">
                  Add object type
                </Button>
              </Dropdown>
              <Button size="small" onClick={removeTypes}>
                Remove selected
              </Button>
            </Space>
          )}
        />
      </SortableTableWrapper>
    </div>
  );
};

export default ObjectTypesSection;
